import React, { useEffect, useRef, useState } from 'react';
import { submitMeetingRequest } from '../../constants/contact';
import { MEETING_FIELD_LIMITS, sanitizeMeetingField } from '../../utils/inputSecurity';
import './MeetingRequestModal.css';

const INITIAL_FORM = {
  name: '',
  company: '',
  contact: '',
  message: '',
};

const SUBMIT_TIMEOUT_MS = 15000;

const MeetingRequestModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle');
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setForm(INITIAL_FORM);
      setHoneypot('');
      setStatus('idle');
      isSubmittingRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: sanitizeMeetingField(field, event.target.value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const safeForm = {
      name: sanitizeMeetingField('name', form.name).trim(),
      company: sanitizeMeetingField('company', form.company).trim(),
      contact: sanitizeMeetingField('contact', form.contact).trim(),
      message: sanitizeMeetingField('message', form.message).trim(),
    };

    if (isSubmittingRef.current || !safeForm.name || !safeForm.contact || honeypot.trim()) {
      return;
    }

    isSubmittingRef.current = true;
    setStatus('sending');

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

    try {
      await submitMeetingRequest(
        {
          name: safeForm.name,
          company: safeForm.company,
          contact: safeForm.contact,
          message: safeForm.message,
          _honey: '',
          _subject: 'Заявка на встречу',
          _template: 'table',
        },
        { signal: controller.signal },
      );

      setStatus('success');
    } catch (error) {
      setStatus('error');
    } finally {
      window.clearTimeout(timeoutId);
      isSubmittingRef.current = false;
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const isSending = status === 'sending';

  return (
    <div className="meeting-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div
        className="meeting-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="meeting-modal-title"
      >
        <button type="button" className="meeting-modal-close" onClick={onClose} aria-label={'Закрыть'}>
          ×
        </button>

        {status === 'success' ? (
          <div className="meeting-modal-success">
            <h2 id="meeting-modal-title" className="meeting-modal-title">
              {'Заявка на встречу'}
            </h2>
            <p className="meeting-modal-success-text">{'Заявка отправлена. Мы свяжемся с вами по указанным контактам.'}</p>
            <button type="button" className="meeting-modal-submit" onClick={onClose}>
              {'Закрыть'}
            </button>
          </div>
        ) : (
          <>
            <h2 id="meeting-modal-title" className="meeting-modal-title">
              {'Заявка на встречу'}
            </h2>
            <p className="meeting-modal-desc">{'Оставьте контакты — вернёмся с предложением по времени и формату встречи.'}</p>

            <form className="meeting-modal-form" onSubmit={handleSubmit}>
              <input
                type="text"
                name="_honey"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="meeting-modal-honey"
              />
              <label className="meeting-modal-field">
                <span>{'Имя'}</span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange('name')}
                  autoComplete="name"
                  required
                  disabled={isSending}
                  maxLength={MEETING_FIELD_LIMITS.name}
                />
              </label>

              <label className="meeting-modal-field">
                <span>{'Компания'}</span>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange('company')}
                  autoComplete="organization"
                  disabled={isSending}
                  maxLength={MEETING_FIELD_LIMITS.company}
                />
              </label>

              <label className="meeting-modal-field">
                <span>{'Контакты'}</span>
                <input
                  type="text"
                  name="contact"
                  value={form.contact}
                  onChange={handleChange('contact')}
                  placeholder={'Телефон, email или Telegram'}
                  autoComplete="tel"
                  required
                  disabled={isSending}
                  maxLength={MEETING_FIELD_LIMITS.contact}
                />
              </label>

              <label className="meeting-modal-field">
                <span>{'Кратко о задаче'}</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange('message')}
                  placeholder={'Например: ниша, текущий бюджет, что хотите посчитать'}
                  rows={4}
                  disabled={isSending}
                  maxLength={MEETING_FIELD_LIMITS.message}
                />
              </label>

              {status === 'error' ? (
                <p className="meeting-modal-message meeting-modal-message--error">{'Не удалось отправить заявку. Попробуйте ещё раз или напишите на почту.'}</p>
              ) : null}

              <button type="submit" className="meeting-modal-submit" disabled={isSending}>
                {isSending ? 'Отправляем...' : 'Отправить заявку'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MeetingRequestModal;
